import { Avatar } from '@shared/ui';
import { cn } from '@shared/lib/cn';
import { useBasicUserQuery } from '../model/queries';
import type { UserBasicInfo } from '../model/types';

interface UserCellProps {
  userId: string | null | undefined;
  /** Pre-resolved brief from an embedded payload — skips the /users/{id} fetch. */
  user?: UserBasicInfo | null;
  size?: 'xs' | 'sm' | 'md';
  showRole?: boolean;
  className?: string;
  fallback?: string;
}

function fallbackInitials(uuid: string): string {
  return uuid.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase() || '??';
}

export function UserCell({
  userId,
  user,
  size = 'sm',
  showRole,
  className,
  fallback = '—',
}: UserCellProps) {
  const query = useBasicUserQuery(user ? undefined : userId);
  const data = user ?? query.data;
  const isLoading = user ? false : query.isLoading;

  if (!userId) {
    return <span className={cn('text-sm text-muted-foreground', className)}>{fallback}</span>;
  }

  const initials = data?.initials ?? fallbackInitials(userId);
  const name = data?.displayName ?? `${userId.slice(0, 8)}…`;

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <Avatar seed={userId} initials={initials} size={size} inactive={data?.active === false} />
      <span className="flex flex-col leading-tight">
        <span
          className={cn(
            'text-sm',
            data?.active === false && 'text-muted-foreground line-through',
            isLoading && 'text-muted-foreground',
          )}
        >
          {name}
        </span>
        {showRole && data?.roles?.length ? (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {data.roles.join(' · ')}
          </span>
        ) : null}
      </span>
    </span>
  );
}
